import type { ReactNode } from "react";
import { useEffect } from "react";
import { BRAND } from "@hik/i18n";
import { BrandWordmark } from "../../ui/Brand";
import { ICONS, type IconName } from "../../ui/icon-data";

/**
 * Hangi yüzeye giriş yapıldığı: etiket, arka plan rengi ve deseni bununla ayrışır. Şirket tarafında tek giriş ekranı vardır;
 * `VITE_PORTAL` (calisan | yonetim) verilirse o kurulum ilgili adla açılır, verilmezse etiket gösterilmez.
 * Platform girişi her zaman kendi etiketini taşır.
 */
export type Portal = "calisan" | "yonetim" | "platform";

const PORTAL_LABEL: Record<Portal, string> = {
  calisan: "Çalışan portalı",
  yonetim: "Şirket yönetim paneli",
  platform: "Platform yönetim paneli",
};

/** Arka plan deseninde yüzeyin konusunu anlatan simgeler (16 adet, 4×4 karo). */
const PORTAL_DOODLES: Record<Portal, IconName[]> = {
  calisan: [
    "calendar-check", "airplane", "receipt", "bell", "wallet", "sun", "file-text", "gift",
    "clock", "megaphone", "device-mobile", "heartbeat", "car", "fork-knife", "thumbs-up", "baby",
  ],
  yonetim: [
    "users-three", "chart-bar", "tree-structure", "list-checks", "briefcase", "identification-card", "clipboard-text", "user-plus",
    "seal-check", "money", "folder-open", "handshake", "table", "bank", "graduation-cap", "calendar-dots",
  ],
  platform: [
    "buildings", "shield-check", "key", "globe", "gear", "wrench", "lock-key", "desktop",
    "eye", "sliders", "storefront", "share-network", "magnifying-glass", "password", "scan", "tree-structure",
  ],
};

const CELL = 84;
const DOODLE = 34;

/** Tekrarlayan simge deseni. Süs amaçlıdır; konumlar sabittir (her açılışta aynı görünür). */
function AuthBackdrop({ portal }: { portal: Portal }) {
  const id = `auth-doodles-${portal}`;
  return (
    <svg className="auth__backdrop" aria-hidden="true" focusable="false">
      <defs>
        <pattern id={id} width={CELL * 4} height={CELL * 4} patternUnits="userSpaceOnUse">
          {PORTAL_DOODLES[portal].map((name, i) => {
            const cx = (i % 4) * CELL + CELL / 2 + (((i * 7) % 5) - 2) * 5;
            const cy = Math.floor(i / 4) * CELL + CELL / 2 + (((i * 11) % 5) - 2) * 5;
            const angle = ((i * 37) % 50) - 25;
            return (
              <g
                key={name}
                transform={`translate(${cx} ${cy}) rotate(${angle}) scale(${DOODLE / 256}) translate(-128 -128)`}
                dangerouslySetInnerHTML={{ __html: ICONS[name] }}
              />
            );
          })}
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
}

function envPortal(): Portal | undefined {
  const v: unknown = import.meta.env?.VITE_PORTAL;
  return v === "calisan" || v === "yonetim" ? v : undefined;
}

/** Giriş, kurulum ve davet ekranlarının ortak düzeni. */
export function AuthLayout({
  title,
  lead,
  children,
  footer,
  portal = envPortal(),
}: {
  title: string;
  lead?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  portal?: Portal;
}) {
  const label = portal ? PORTAL_LABEL[portal] : null;
  useEffect(() => {
    document.title = [title, label, BRAND.name].filter(Boolean).join(" · ");
  }, [title, label]);
  return (
    <div className="auth" data-portal={portal}>
      {portal && <AuthBackdrop portal={portal} />}
      <main className="auth__panel" id="main">
        <div className="auth__brand">
          <BrandWordmark height={32} />
          {label && <span className="auth__portal">{label}</span>}
        </div>
        <h1 className="auth__title">{title}</h1>
        {lead && <p className="auth__lead">{lead}</p>}
        {children}
        {footer && <div className="auth__footer">{footer}</div>}
      </main>
    </div>
  );
}
